import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type {
  EspdResponseDto,
  PortalSourcingEventDetailDto,
} from "../../../../../autogen/bff-api";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { StatusAccentBar } from "../../../../../common/components/StatusAccentBar";
import type { StatusVariant } from "../../../../../common/components/StatusVariant";
import { EspdCriterionForm } from "../espd/EspdCriterionForm";
import { presentationForCriterion } from "../espd/espdPresentationText";
import { emptyEspd } from "../espd/useEspdAnswers";

// Shared between the bid wizard and the participation-request wizard: both editors carry an
// espdResponse, which is all this step touches.
type EspdHolder = { espdResponse?: EspdResponseDto | null };
type EspdHolderPatch = { espdResponse?: EspdResponseDto };

interface Props {
  entityId: string;
  event: PortalSourcingEventDetailDto;
  readOnly: boolean;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const SupplierInfoStep = ({
  entityId,
  event,
  readOnly,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();

  const supplierDeclarationCriteria = event.applicableSupplierDeclarationCriteria ?? [];
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<
    EspdHolder,
    EspdHolderPatch
  >(entityId);

  const espd: EspdResponseDto = state.espdResponse ?? emptyEspd();
  const eo = espd.economicOperator ?? {
    smeIndicator: null,
    representatives: [],
  };
  const rep = eo.representatives?.[0] ?? { firstName: "", familyName: "" };

  const saveEspd = (next: EspdResponseDto) => {
    if (readOnly) return;
    updateField({ espdResponse: next }, { debounceMs: 300 });
  };
  const setSme = (yes: boolean) =>
    saveEspd({ ...espd, economicOperator: { ...eo, smeIndicator: yes } });
  const setRep = (patch: Partial<typeof rep>) =>
    saveEspd({
      ...espd,
      economicOperator: { ...eo, representatives: [{ ...rep, ...patch }] },
    });

  const isSaving =
    fieldStatus("espdResponse") === "saving" ||
    fieldStatus("espdResponse") === "pending";

  return (
    <Box pb="8" pt="6">
      <Flex align="center" gap="2" mb="6">
        <Heading size="md">{t("About the supplier")}</Heading>
        {isSaving && <Spinner size="sm" color="smMuted" />}
      </Flex>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Identification")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="8" spacingY="4">
            <Field label={t("Supplier")} value={selectedOrg.name} />
            {selectedOrg.organizationNumber && (
              <Field
                label={t("Organization number")}
                value={selectedOrg.organizationNumber}
              />
            )}
            {selectedOrg.country?.name && (
              <Field label={t("Country")} value={selectedOrg.country.name} />
            )}
          </SimpleGrid>
        </CardBody>
      </Card>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="1">
            {t("Authorized representative")}
          </Heading>
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("The person who can sign the offer on behalf of the supplier.")}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="6" spacingY="4">
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("First name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.firstName}
                onChange={(e) => setRep({ firstName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Family name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.familyName}
                onChange={(e) => setRep({ familyName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Email")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                type="email"
                value={rep.email ?? ""}
                onChange={(e) => setRep({ email: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("Role")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.role ?? ""}
                onChange={(e) => setRep({ role: e.target.value })}
              />
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Box mb="8">
        <Heading size="sm" mb="4">
          {t("Self-declaration")}
        </Heading>
        <Stack spacing="5">
          <Question
            text={t(
              "Is the supplier a micro, small or medium-sized enterprise (SME)?",
            )}
            value={eo.smeIndicator ?? null}
            onChange={setSme}
            yesLabel={t("Yes")}
            noLabel={t("No")}
            readOnly={readOnly}
          />
          {supplierDeclarationCriteria.map((criterion) => (
            <EspdCriterionForm
              key={criterion.id}
              criterion={criterion}
              espd={espd}
              onChange={saveEspd}
              readOnly={readOnly}
              presentation={presentationForCriterion(criterion)}
              showStatusBorder
            />
          ))}
        </Stack>
      </Box>

      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};

const Field = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Text
      fontSize="xs"
      color="smMuted"
      textTransform="uppercase"
      letterSpacing="wide"
    >
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium">
      {value}
    </Text>
  </Box>
);

const Question = ({
  text,
  value,
  onChange,
  yesLabel,
  noLabel,
  readOnly,
}: {
  text: string;
  value: boolean | null;
  onChange: (yes: boolean) => void;
  yesLabel: string;
  noLabel: string;
  readOnly: boolean;
}) => {
  const variant: StatusVariant = value !== null ? "ok" : "neutral";
  return (
    <Card variant="outline" rounded="xl" position="relative" overflow="hidden">
      <StatusAccentBar variant={variant} />
      <CardBody>
        <Heading size="sm" mb="4">
          {text}
        </Heading>
        <HStack spacing="2">
          <Button
            size="sm"
            variant={value === true ? "solid" : "outline"}
            colorScheme="teal"
            isDisabled={readOnly}
            onClick={() => onChange(true)}
          >
            {yesLabel}
          </Button>
          <Button
            size="sm"
            variant={value === false ? "solid" : "outline"}
            colorScheme="teal"
            isDisabled={readOnly}
            onClick={() => onChange(false)}
          >
            {noLabel}
          </Button>
        </HStack>
      </CardBody>
    </Card>
  );
};
